/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import {
  HttpStatusCode,
  Paytos,
  TalerCorebankApi,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  Loading,
  RouteDefinition,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { RetryableError } from "../../components/RetryableError.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";
import { LoginForm } from "../LoginForm.js";
import { ProfileNavigation } from "../ProfileNavigation.js";
import { ReadOnlyDetail, ReadOnlyDetails } from "./ReadOnlyDetails.js";

export interface MerchantIntegrationRoutes {
  routeOverview: RouteDefinition;
  routeMyAccountDetails: RouteDefinition;
  routeMyAccountMerchantIntegration: RouteDefinition;
  routeMyAccountPassword: RouteDefinition;
}

export function MerchantIntegration({
  account,
  routeMyAccountDetails,
  routeMyAccountMerchantIntegration,
  routeMyAccountPassword,
  routeOverview,
}: MerchantIntegrationRoutes & { account: string }): VNode {
  const { i18n } = useTranslationContext();
  const result = useAccountDetails(account);

  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return (
      <Fragment>
        <RetryableError
          error={result}
          title={i18n.str`Failed to load account details.`}
          onRetry={() => void revalidateAccountDetails()}
        />
        <LoginForm currentUser={account} />
      </Fragment>
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.NotFound:
        return <LoginForm currentUser={account} />;
      default:
        assertUnreachable(result);
    }
  }

  return (
    <MerchantIntegrationView
      account={account}
      accountData={result.body}
      routeOverview={routeOverview}
      routeMyAccountDetails={routeMyAccountDetails}
      routeMyAccountMerchantIntegration={routeMyAccountMerchantIntegration}
      routeMyAccountPassword={routeMyAccountPassword}
    />
  );
}

export function MerchantIntegrationView({
  account,
  accountData,
  routeMyAccountDetails,
  routeMyAccountMerchantIntegration,
  routeMyAccountPassword,
  routeOverview,
}: MerchantIntegrationRoutes & {
  account: string;
  accountData: TalerCorebankApi.AccountData;
}): VNode {
  const { i18n } = useTranslationContext();
  const {
    lib: { bank },
  } = useBankCoreApiContext();
  const parsedPayto = Paytos.fromString(accountData.payto_uri);
  const payto =
    parsedPayto.tag === "error" || !parsedPayto.value.targetType
      ? undefined
      : parsedPayto.value;
  const revenueURL = bank.getRevenueAPI(account);
  revenueURL.username = account;

  return (
    <Fragment>
      <ProfileNavigation
        current="merchant-integration"
        routeOverview={routeOverview}
        routeMyAccountDetails={routeMyAccountDetails}
        routeMyAccountMerchantIntegration={routeMyAccountMerchantIntegration}
        routeMyAccountPassword={routeMyAccountPassword}
      />

      <div class="grid grid-cols-1 gap-x-8 gap-y-8 rounded-lg bg-gray-100 px-4 pb-4 pt-6 md:grid-cols-3">
        <div class="px-4 sm:px-0">
          <h2 class="text-base font-semibold leading-7 text-onBackground">
            <i18n.Translate>Merchant integration</i18n.Translate>
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            <i18n.Translate>
              Use this information to link your Taler Merchant Backoffice
              account with the current bank account. You can start by copying
              the values, then go to your merchant backoffice service provider,
              login into your account and look for the "import" button in the
              "bank account" section.
            </i18n.Translate>
          </p>
        </div>

        {accountData.is_taler_exchange || account === "admin" || !payto ? (
          <div class="md:col-span-2">
            <Attention title={i18n.str`Not available`} type="info">
              <i18n.Translate>
                Merchant integration is not available for this account.
              </i18n.Translate>
            </Attention>
          </div>
        ) : (
          <ReadOnlyDetails>
            <ReadOnlyDetail
              label={i18n.str`Account type`}
              value={payto.targetType}
              description={i18n.str`Method to use for wire transfer.`}
            />
            {(() => {
              switch (payto.targetType) {
                case "iban":
                  return (
                    <ReadOnlyDetail
                      label={i18n.str`IBAN`}
                      value={payto.iban}
                      copyValue={payto.iban}
                      description={i18n.str`International Bank Account Number.`}
                    />
                  );
                case "x-taler-bank":
                  return (
                    <Fragment>
                      <ReadOnlyDetail
                        label={i18n.str`Bank host`}
                        value={payto.host}
                        copyValue={payto.host}
                        description={i18n.str`Bank host where the service is located.`}
                      />
                      <ReadOnlyDetail
                        label={i18n.str`Account name`}
                        value={payto.account}
                        copyValue={payto.account}
                        description={i18n.str`Bank account identifier for wire transfers.`}
                      />
                    </Fragment>
                  );
                case "bitcoin":
                  return (
                    <ReadOnlyDetail
                      label={i18n.str`Address`}
                      value={payto.address}
                      copyValue={payto.address}
                      description={i18n.str`Bitcoin address for this account.`}
                    />
                  );
                default:
                  return (
                    <ReadOnlyDetail
                      label={i18n.str`Account address`}
                      value={accountData.payto_uri}
                      copyValue={accountData.payto_uri}
                    />
                  );
              }
            })()}
            <ReadOnlyDetail
              label={i18n.str`Owner's name`}
              value={accountData.name}
              copyValue={accountData.name}
              description={i18n.str`Legal name of the person holding the account.`}
            />
            <ReadOnlyDetail
              label={i18n.str`Account info URL`}
              value={revenueURL.href}
              copyValue={revenueURL.href}
              description={i18n.str`From where the merchant can download information about incoming wire transfers to this account.`}
            />
          </ReadOnlyDetails>
        )}
      </div>
    </Fragment>
  );
}
